/**
 * historyRiskAssessmentConfig.js
 *
 * Shared configuration for the History Risk Assessment section in Adult Autism.
 * Mirrors the ADHD Clinical Interview Risk Assessment (11_RSK.js) categories and
 * option lists, but uses plain panel titles (no "11.1" numbering) to match the
 * Adult Autism History visual style.
 *
 * Data is stored on the patient's assessmentStages/history document under the
 * `riskAssessment` field (sibling to `data`, which holds prefill sections).
 */

import { normaliseStructuredNotes } from '../../12_observation/components/StructuredNotesEditor';

/** Sidebar id for the virtual Risk Assessment step appended after prefill sections. */
export const HISTORY_RISK_ASSESSMENT_SECTION_ID = 'risk-assessment';

/**
 * Stable domainId for the Risk Assessment "Additional Notes" StructuredNotesEditor.
 * Used as the move-target key so notes can be moved to/from this box via the
 * same picker as the prefill subsection panels.
 */
export const RISK_ADDITIONAL_NOTES_PANEL_KEY = 'risk-additional-notes';

/** Firestore dot-path for the Additional Notes array on assessmentStages/history. */
export const RISK_ADDITIONAL_NOTES_FIRESTORE_PATH = 'riskAssessment.clinicianNotes.Additional Notes';

/**
 * Panel definitions consumed by the UI. Each panel maps to
 * riskAssessment.subsections[panelIndex + 1] in Firestore.
 */
export const HISTORY_RISK_QUESTION_PANELS = [
  {
    title: 'Risks to Self',
    categories: {
      'Self-harm ideation': {
        yesOptions: [
          'Passive',
          'Active with Plan but no Intent',
          'Active with Intent but no Means',
          'Active with Means and Intent',
          'Active with Means but no Intent',
        ],
        noOptions: ['None'],
      },
      'Suicidal ideation': {
        yesOptions: [
          'Passive',
          'Active with Plan but no Intent',
          'Active with Intent but no Means',
          'Active with Means and Intent',
          'Active with Means but no Intent',
        ],
        noOptions: ['None'],
      },
    },
  },
  {
    title: 'Risks to Others',
    categories: {
      'Risk to Others': {
        yesOptions: [
          'Passive',
          'Active with Plan but no Intent',
          'Active with Intent but no Means',
          'Active with Means and Intent',
          'Active with Means but no Intent',
        ],
        noOptions: ['None'],
      },
    },
  },
  {
    title: 'Other Risks',
    categories: {
      'Self-neglect': { yesOptions: ['Mild', 'Moderate', 'Severe'], noOptions: ['None'] },
      'Substance abuse': { yesOptions: ['Mild', 'Moderate', 'Severe'], noOptions: ['None'] },
      'Harm from others': { yesOptions: ['Mild', 'Moderate', 'Severe'], noOptions: ['None'] },
    },
  },
];

/**
 * Builds the default Firestore shape for a new patient's history risk assessment.
 * Seeded server-side on patient creation; also used client-side when older
 * documents lack the field.
 */
export function createEmptyHistoryRiskAssessmentData() {
  const subsections = {};

  HISTORY_RISK_QUESTION_PANELS.forEach((panel, panelIndex) => {
    const categories = {};
    Object.entries(panel.categories).forEach(([categoryName, category], catIndex) => {
      // categoryNumber is required by the LLM input extractor
      // (llm_inputs_asd_ci.extract_risk_assessment_inputs) and the paragraph
      // processor (llm_processor_asd_ci._build_risk_user_input). Keys are built
      // as asd_ci_RSK_{subsection}_{categoryNumber}_* — without this field the
      // pipeline cannot match clinician Yes/No and detail selections.
      categories[categoryName] = {
        categoryNumber: String(catIndex + 1),
        isPresentSelection: null,
        selectionDetails: [],
        yesOptions: category.yesOptions,
        noOptions: category.noOptions,
      };
    });
    subsections[String(panelIndex + 1)] = {
      title: panel.title,
      categories,
    };
  });

  return {
    status: 'notStarted',
    progress: 0,
    subsections,
    clinicianNotes: { 'Additional Notes': '' },
  };
}

/**
 * Converts persisted Firestore risk data into the local UI selection shape
 * used by HistoryRiskAssessmentPanel (same structure as 11_RSK.js).
 */
export function initializeRiskSelectionsFromData(riskData) {
  const subsections = riskData?.subsections || {};

  return HISTORY_RISK_QUESTION_PANELS.map((panel, panelIndex) => (
    Object.entries(panel.categories).reduce((acc, [categoryName]) => {
      const categoryData = subsections[String(panelIndex + 1)]?.categories?.[categoryName] || {};
      acc[categoryName] = {
        present: categoryData.isPresentSelection ?? null,
        selectedOption: (categoryData.selectionDetails || []).map((option) => ({
          value: option,
          label: option,
        })),
      };
      return acc;
    }, {})
  ));
}

/**
 * Returns true when the clinician has entered ANY input on the Risk Assessment
 * page: a present/absent selection, specific selection details, or at least one
 * additional note.
 *
 * This drives the sidebar "complete" (green) state for Risk Assessment which —
 * unlike the prefill-based history sections that go green once merely visited —
 * should only light up once real data has been recorded. Deriving it live from
 * the risk data keeps it accurate without persisting a separate "visited" flag.
 *
 * @param {object} riskData A (normalised) risk assessment document.
 * @returns {boolean}
 */
export function hasAnyRiskAssessmentInput(riskData) {
  if (!riskData || typeof riskData !== 'object') return false;

  const subsections = riskData.subsections || {};
  for (const subsection of Object.values(subsections)) {
    const categories = subsection?.categories || {};
    for (const category of Object.values(categories)) {
      if (category?.isPresentSelection != null) return true;
      if (Array.isArray(category?.selectionDetails) && category.selectionDetails.length > 0) {
        return true;
      }
    }
  }

  const additionalNotes = riskData.clinicianNotes?.['Additional Notes'];
  if (Array.isArray(additionalNotes) && additionalNotes.length > 0) return true;

  return false;
}

/**
 * Normalises incoming Firestore / demo data into a complete risk assessment
 * document, filling any missing categories from the template.
 */
export function normalizeHistoryRiskAssessmentData(raw) {
  const template = createEmptyHistoryRiskAssessmentData();
  if (!raw) return template;

  const subsections = { ...template.subsections };
  Object.entries(raw.subsections || {}).forEach(([subKey, subsection]) => {
    const templateSub = template.subsections[subKey] || {};
    const templateCategories = templateSub.categories || {};
    const persistedCategories = subsection.categories || {};
    const mergedCategories = {};

    // Deep-merge each category so template fields (especially categoryNumber
    // and option lists) survive when older Firestore docs only stored answers.
    Object.entries(templateCategories).forEach(([catName, templateCat]) => {
      mergedCategories[catName] = {
        ...templateCat,
        ...(persistedCategories[catName] || {}),
      };
    });
    Object.entries(persistedCategories).forEach(([catName, persistedCat]) => {
      if (!mergedCategories[catName]) {
        mergedCategories[catName] = persistedCat;
      }
    });

    subsections[subKey] = {
      ...templateSub,
      ...subsection,
      categories: mergedCategories,
    };
  });

  return {
    ...template,
    ...raw,
    subsections,
    clinicianNotes: {
      ...template.clinicianNotes,
      ...(raw.clinicianNotes || {}),
      // Additional Notes is now a structured note array. Coerce any legacy
      // plain-string value (or the empty-string template default) into an
      // array so every reader gets a consistent shape.
      'Additional Notes': normaliseStructuredNotes(raw.clinicianNotes?.['Additional Notes']),
    },
  };
}
